import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { CATEGORIES, MODELS, ORDER_NEW, waLink, orderStoragesFor, iphoneSpecFor, formatPrice } from "@/lib/store";
import { fold } from "@/lib/search";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { track } from "@/lib/analytics";
import { ModelPicker } from "./ModelPicker";
import { fetchOrderPrices, findPrice, formatRange, conditionKey } from "@/lib/orderPrices";
import { NO_STORAGE } from "@/lib/tradeCatalog";
import { ORDER_CATALOG, catalogEntry, fitSpecs, fullName } from "@/lib/orderCatalog";
import { OrderSpecFields, OrderSummary, chip, specLines } from "./OrderSpecFields";

const entryOf = (s) => catalogEntry(s.category, s.model);
const pickList = (category) => (category === "iphone" ? [...MODELS.iphone].reverse() : ORDER_CATALOG[category]?.map((e) => e.name));

const specOf = (s) => (s.category === "iphone" ? iphoneSpecFor(s.model) : null);
const conditionsFor = (s) => (specOf(s) && !specOf(s).sealed ? ["Seminovo"] : [ORDER_NEW, "Seminovo"]);
const URL_CONDITIONS = { seminovo: "Seminovo" };

const words = (s) => fold(s).split(/[^a-z0-9ª]+/).filter((t) => t && t !== "iphone" && t !== "apple");
const matchModel = (list, query) => {
  const q = new Set(words(query));
  return list.filter((m) => words(m).every((t) => q.has(t))).sort((a, b) => words(b).length - words(a).length)[0] || "";
};
// Categorias com lista só aceitam modelos da lista; o que não bater vai para as observações.
const withModel = (s, raw) => {
  const list = pickList(s.category);
  if (!list || !raw) return { ...s, model: raw };
  const model = matchModel(list, raw);
  return { ...s, model, notes: model ? s.notes : raw };
};

const fit = (s) => {
  const storages = orderStoragesFor(s.category, s.model);
  const colors = specOf(s)?.colors;
  const conditions = conditionsFor(s);
  const entry = entryOf(s);
  return {
    ...s,
    specs: entry ? fitSpecs(entry, s.specs) : {},
    storage: storages.includes(s.storage) ? s.storage : storages[0],
    color: colors && !colors.includes(s.color) ? "" : s.color,
    condition: conditions.includes(s.condition) ? s.condition : conditions[0],
  };
};

const fromUrl = () => {
  const q = new URLSearchParams(window.location.search);
  const cat = q.get("cat");
  return fit(withModel({
    category: CATEGORIES.some((c) => c.id === cat) ? cat : "iphone",
    storage: q.get("storage") || "",
    color: q.get("color") || "",
    condition: URL_CONDITIONS[q.get("cond")] || ORDER_NEW,
    notes: "",
    name: "",
    specs: {},
  }, q.get("model") || ""));
};

const input = "w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-zinc-500 outline-none transition-colors focus:border-white/40";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs text-zinc-400">{label}</span>
    {children}
  </label>
);

const orderLines = (f) => {
  const cat = CATEGORIES.find((c) => c.id === f.category)?.label;
  const entry = entryOf(f);
  const base = entry ? specLines(entry, f.specs) : [["Armazenamento", f.storage], ["Cor", f.color]];
  return [
    ["Categoria", cat],
    ["Produto", entry ? fullName(entry, f.specs) : f.model],
    ...base,
    ["Condição", f.category === "acessorios" ? "" : f.condition],
    ["Estimativa vista no site", f.estimate],
    ["Detalhes", f.notes],
  ].filter(([, v]) => v).map(([k, v]) => `• ${k}: ${v}`);
};

export const MAX_ITEMS = 5;
const titleOf = (f) => (entryOf(f) ? fullName(entryOf(f), f.specs) : f.model || "Pedido");
const avgOf = (p) => (p ? (Number(p.min) + Number(p.max)) / 2 : 0);

const priceOf = (f, prices) => {
  const entry = entryOf(f);
  if (f.category === "iphone") return findPrice(prices, f.model, f.storage, conditionKey(f.condition));
  return entry ? findPrice(prices, fullName(entry, f.specs), f.specs.storage || NO_STORAGE, conditionKey(f.condition)) : null;
};

// Um aparelho: mensagem de sempre. Vários: um bloco por aparelho, com a média de cada um e a soma.
export const buildOrderMessage = (items, name) => {
  if (items.length === 1) return ["Olá, RC iPhones! Quero fazer um pedido:", ...orderLines(items[0]), name ? `Meu nome: ${name}` : ""].filter(Boolean).join("\n");
  const sum = items.reduce((s, it) => s + avgOf(it.price), 0);
  const missing = items.some((it) => !it.price);
  return [
    `Olá, RC iPhones! Quero fazer um pedido de ${items.length} aparelhos:`,
    ...items.flatMap((it, i) => ["", `*${i + 1}) ${titleOf(it)}*`, ...orderLines({ ...it, estimate: it.price ? `${formatRange(it.price)} (média ${formatPrice(avgOf(it.price))})` : "" })]),
    "",
    sum > 0 ? `*Soma estimada (média):* ${formatPrice(sum)}${missing ? " + itens sob consulta" : ""}` : "",
    name ? `Meu nome: ${name}` : "",
  ].filter((l, i, a) => l || (i > 0 && a[i - 1])).join("\n").trim();
};

const AddedItem = ({ it, n, onRemove }) => (
  <li data-testid={`order-item-${n}`} className="flex items-start justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
    <div className="min-w-0">
      <p className="text-sm font-semibold text-white">{n}. {titleOf(it)}</p>
      <p className="mt-0.5 text-xs text-zinc-400">{[it.category === "acessorios" ? "" : it.storage, it.color, it.category === "acessorios" ? "" : it.condition].filter(Boolean).join(" · ")}</p>
      <p className="mt-1 text-xs text-emerald-300">{it.price ? `Média ${formatPrice(avgOf(it.price))} (${formatRange(it.price)})` : "Preço sob consulta"}</p>
    </div>
    <button type="button" data-testid={`order-item-remove-${n}`} onClick={onRemove} aria-label="Remover" className="rounded-full p-1.5 text-zinc-400 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
  </li>
);

export const OrderForm = () => {
  const [f, setF] = useState(fromUrl);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const setModel = (model) => setF((s) => {
    const next = { ...s, model };
    if (conditionsFor(s).length === 1 && conditionsFor(next).length > 1) next.condition = ORDER_NEW;
    const min = orderStoragesFor(next.category, next.model)[0];
    return fit(min !== orderStoragesFor(s.category, s.model)[0] ? { ...next, storage: min } : next);
  });
  const isAcc = f.category === "acessorios";
  const entry = entryOf(f);
  const list = pickList(f.category);
  const pickSpec = (field, value) => setF((s) => fit({ ...s, specs: { ...s.specs, [field]: value } }));
  const storages = orderStoragesFor(f.category, f.model);
  const colors = specOf(f)?.colors;
  const conditions = conditionsFor(f);
  const [prices, setPrices] = useState([]);
  const [added, setAdded] = useState([]);
  const price = priceOf(f, prices);

  useEffect(() => {
    fetchOrderPrices().then(setPrices).catch(() => setPrices([]));
  }, []);

  useEffect(() => {
    const onOrder = (e) => setF((s) => fit(withModel({ ...s, category: e.detail?.category || s.category, storage: "" }, e.detail?.model || "")));
    window.addEventListener("rc-order", onOrder);
    return () => window.removeEventListener("rc-order", onOrder);
  }, []);

  const current = () => ({ ...f, price, estimate: price ? formatRange(price) : "" });
  const addAnother = () => {
    if (!f.model.trim()) {
      setError("Escolha o modelo antes de adicionar outro aparelho.");
      return;
    }
    setError("");
    setAdded((l) => [...l, current()]);
    setF((s) => fit({ ...s, model: "", storage: "", color: "", notes: "", specs: {} }));
  };
  const submit = (e) => {
    e.preventDefault();
    const items = f.model.trim() || f.notes.trim() ? [...added, current()] : added;
    if (!items.length) {
      setError("Diga o modelo ou descreva o que você procura.");
      return;
    }
    setError("");
    track("contact", { source: "pedido" });
    window.open(waLink(buildOrderMessage(items, f.name)), "_blank", "noopener,noreferrer");
  };
  const total = added.length + (f.model.trim() ? 1 : 0);
  const sum = [...added, ...(f.model.trim() ? [{ price }] : [])].reduce((s, it) => s + avgOf(it.price), 0);

  return (
    <form onSubmit={submit} data-testid="order-form" className="space-y-5 rounded-[28px] border border-white/10 bg-[#111113] p-6 sm:p-8">
      {added.length > 0 && (
        <div data-testid="order-items">
          <span className="mb-1.5 block text-xs text-zinc-400">Aparelhos no pedido</span>
          <ul className="space-y-2">{added.map((it, i) => <AddedItem key={i} it={it} n={i + 1} onRemove={() => setAdded((l) => l.filter((_, j) => j !== i))} />)}</ul>
          <p className="mt-3 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-300">Aparelho {added.length + 1}</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button type="button" key={c.id} data-testid={`order-category-${c.id}`} onClick={() => setF((s) => fit({ ...s, category: c.id, model: "", storage: "", specs: {} }))} className={chip(f.category === c.id)}>{c.label}</button>
        ))}
      </div>
      <div>
        <span className="mb-1.5 block text-xs text-zinc-400">{isAcc ? "Qual acessório?" : "Qual modelo?"}</span>
        <ModelPicker value={f.model} onChange={setModel} options={list || MODELS[f.category] || []} selectOnly={Boolean(list)}
          groupOf={(o) => catalogEntry(f.category, o)?.group}
          placeholder={list ? `Toque para escolher o ${CATEGORIES.find((c) => c.id === f.category)?.label}` : `Ex: ${MODELS[f.category]?.[MODELS[f.category].length - 1] || ""}`} className={input} />
      </div>
      {entry && <OrderSpecFields entry={entry} specs={f.specs} onPick={pickSpec} />}
      {!entry && <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {!isAcc && (
          <Field label="Armazenamento">
            <select data-testid="order-storage-select" value={f.storage} onChange={set("storage")} className={input}>
              {storages.map((s) => <option key={s} value={s} className="bg-zinc-900">{s}</option>)}
            </select>
          </Field>
        )}
        {!colors && (
          <Field label="Cor (opcional)">
            <input data-testid="order-color-input" value={f.color} onChange={set("color")} placeholder="Ex: Preto" className={input} />
          </Field>
        )}
      </div>}
      {colors && (
        <div data-testid="order-color-options">
          <span className="mb-1.5 block text-xs text-zinc-400">Cor (opcional)</span>
          <div className="flex flex-wrap gap-2">
            {colors.map((c) => (
              <button type="button" key={c} data-testid={`order-color-${c.toLowerCase().replace(/\s+/g, "-")}`} aria-pressed={f.color === c}
                onClick={() => setF((s) => ({ ...s, color: s.color === c ? "" : c }))} className={chip(f.color === c)}>{c}</button>
            ))}
          </div>
        </div>
      )}
      {!isAcc && (
        <div>
          <span className="mb-1.5 block text-xs text-zinc-400">Condição</span>
          <div className="flex flex-wrap gap-2">
            {conditions.map((c) => <button type="button" key={c} data-testid={`order-condition-${conditionKey(c)}`} onClick={() => setF((s) => ({ ...s, condition: c }))} className={chip(f.condition === c)}>{c}</button>)}
          </div>
          {conditions.length === 1 && <p data-testid="order-sealed-note" className="mt-2 text-xs text-zinc-400">Novo • Lacrado só a partir do iPhone 16 (exceto 16 Pro e 16 Pro Max).</p>}
        </div>
      )}
      {price && (
        <div data-testid="order-estimate" className="rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.06] px-4 py-3">
          <p className="text-sm font-semibold text-emerald-300">Estimativa: {formatRange(price)}</p>
          <p className="mt-0.5 text-xs text-zinc-400">Valor final confirmado no WhatsApp.</p>
        </div>
      )}
      {f.model && !isAcc && (
        <OrderSummary title={entry ? fullName(entry, f.specs) : f.model}
          lines={[...(entry ? specLines(entry, f.specs) : [["", f.storage], ["", f.color]].filter(([, v]) => v)), ["", f.condition]]} />
      )}
      <Field label="Quer contar mais alguma coisa? (opcional)">
        <textarea data-testid="order-notes-input" rows={3} maxLength={500} value={f.notes} onChange={set("notes")} placeholder="Ex: preciso até sexta, quero parcelar em 21x, aceito seminovo com bateria acima de 85%…" className={input} />
      </Field>
      {added.length < MAX_ITEMS - 1 && (
        <button type="button" data-testid="order-add-item" onClick={addAnother}
          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-white/25 py-3.5 text-sm font-semibold text-white transition-colors hover:border-white/50 hover:bg-white/[0.04]">
          <Plus className="h-4 w-4" /> Adicionar outro aparelho ao pedido ({Math.max(total, 1)}/{MAX_ITEMS})
        </button>
      )}
      {total > 1 && sum > 0 && (
        <p data-testid="order-sum" className="rounded-2xl bg-emerald-400/[0.06] px-4 py-3 text-sm text-zinc-200">Soma estimada (média) de {total} aparelhos: <strong className="text-emerald-300">{formatPrice(sum)}</strong></p>
      )}
      <Field label="Seu nome (opcional)">
        <input data-testid="order-name-input" value={f.name} onChange={set("name")} placeholder="Como podemos te chamar?" className={input} />
      </Field>
      {error && <p data-testid="order-error" className="text-sm text-amber-300">{error}</p>}
      <button data-testid="order-submit-button" className="group flex w-full items-center justify-center gap-3 rounded-full bg-[#25D366] py-4 text-base font-bold text-black transition-transform duration-300 hover:scale-[1.02] active:scale-95">
        <WhatsAppIcon className="h-5 w-5" /> {total > 1 ? `Enviar pedido de ${total} aparelhos no WhatsApp` : "Enviar pedido no WhatsApp"}
      </button>
    </form>
  );
};
